import React, { useEffect, useState, useMemo, useCallback } from 'react'
import {
  Plus,
  X,
  ShoppingBag,
  FileText,
  CreditCard,
  Trash2,
  ReceiptIndianRupee,
  Building,
  Folder,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CircleDollarSign,
  Clock,
  AlertTriangle,
  CheckCircle2,
  User,
} from 'lucide-react'
// import { toast } from 'sonner'
import * as api from '../../../app/axios/api'
import { toast } from 'sonner'
import { useRole } from '../../../context/RoleContext'
import AuthorizedComponent from '../../../components/AuthorizedComponent'
import { getCookie } from '../../../app/axios/CookieConfig'

// --- Helper Functions ---
const formatDate = (date) => {
  if (!(date instanceof Date)) {
    date = new Date(date)
  }
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// FIXED: Get date that is X days before today (including today)
const getDateDaysAgo = (days) => {
  const date = new Date()
  date.setDate(date.getDate() - (days - 1)) // -1 to include today
  return formatDate(date)
}

// Delete Confirmation Dialog Component
const DeleteConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  expenseName,
  isDeleting,
}) => {
  if (!isOpen) return null

  return (
    <div className='fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-60'>
      <div className='bg-white rounded-lg shadow-2xl w-full max-w-md mx-4'>
        <div className='p-6'>
          <div className='flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full'>
            <AlertTriangle className='w-6 h-6 text-red-600' />
          </div>

          <h3 className='text-lg font-semibold text-gray-900 text-center mb-2'>
            Delete Expense
          </h3>

          <p className='text-sm text-gray-600 text-center mb-6'>
            Are you sure you want to delete the expense for{' '}
            <span className='font-semibold text-gray-900'>"{expenseName}"</span>
            ? This action cannot be undone.
          </p>

          <div className='flex flex-col sm:flex-row gap-3 sm:gap-2'>
            <button
              type='button'
              onClick={onClose}
              disabled={isDeleting}
              className='flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={onConfirm}
              disabled={isDeleting}
              className='flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed transition-colors text-sm font-medium'
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const Expense = () => {
  const { role } = useRole()
  const id = getCookie('id')
  const [showForm, setShowForm] = useState(false)
  const [approvingId, setApprovingId] = useState(null)
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    expenseId: null,
    expenseName: '',
    isDeleting: false,
  })

  // --- Core State for Date Filtering ---
  const [filterMode, setFilterMode] = useState('single')
  const [statusFilter, setStatusFilter] = useState('all')

  // State for 'single' day mode
  const [selectedDate, setSelectedDate] = useState(new Date())

  // State for 'range' mode - CHANGED: Default from 1st to current date
  const [fromDate, setFromDate] = useState(() => {
    const firstDayOfMonth = new Date()
    firstDayOfMonth.setDate(1)
    return formatDate(firstDayOfMonth)
  })
  const [toDate, setToDate] = useState(() => formatDate(new Date()))

  const [formData, setFormData] = useState({
    amount: '',
    date: new Date(),
    for_product: '',
    note: '',
    purchase_from: '',
    category: '',
    payment_mode: 'cash',
  })

  // --- Data Fetching Logic ---

  // Gets a full 24-hour range for a single date for the API
  const getSingleDayRangeForAPI = (date) => {
    const from = formatDate(date) // e.g., "2025-08-25"

    const nextDay = new Date(date)
    nextDay.setDate(nextDay.getDate() + 1)
    const to = formatDate(nextDay) // e.g., "2025-08-26"

    return { from, to }
  }

  const fetchExpenses = useCallback(async (from, to) => {
    try {
      setLoading(true)
      const res = await api.fetchExpanse(from, to)
      setExpenses(res.data || [])
    } catch (error) {
      console.error('Error fetching expenses:', error)
      toast.error(error?.response?.data?.message || 'Failed to fetch expenses.')
      setExpenses([])
    } finally {
      setLoading(false)
    }
  }, [])

  // --- Main useEffect to Fetch Data on Date/Filter Changes ---
  // useEffect(() => {
  //   if (filterMode === 'single') {
  //     const { from, to } = getSingleDayRangeForAPI(selectedDate)
  //     fetchExpenses(from, to)
  //   } else {
  //     // filterMode === 'range'
  //     if (new Date(fromDate) > new Date(toDate)) {
  //       return // Don't fetch if range is invalid
  //     }
  //     fetchExpenses(fromDate, toDate)
  //   }
  // }, [selectedDate, fromDate, toDate, filterMode, fetchExpenses])

  // useEffect - Yeh data fetch karta hai
  useEffect(() => {
    if (filterMode === 'single') {
      const { from, to } = getSingleDayRangeForAPI(selectedDate)
      fetchExpenses(from, to)
    } else {
      // filterMode 'range' ho ya 'self', dono mein date range fetch hogi
      if (new Date(fromDate) > new Date(toDate)) return
      fetchExpenses(fromDate, toDate)
    }
  }, [selectedDate, fromDate, toDate, filterMode, fetchExpenses])

  // --- Event Handlers for Single Day Navigation ---
  const handlePrevDay = () => {
    const prevDate = new Date(selectedDate)
    prevDate.setDate(selectedDate.getDate() - 1)
    setSelectedDate(prevDate)
  }

  const handleNextDay = () => {
    const nextDate = new Date(selectedDate)
    nextDate.setDate(selectedDate.getDate() + 1)
    setSelectedDate(nextDate)
  }

  const handleToday = () => {
    setSelectedDate(new Date())
  }

  const handleDateSelect = (dateString) => {
    // Correctly handle timezone by parsing as UTC
    const date = new Date(dateString + 'T00:00:00')
    setSelectedDate(date)
  }

  const handleRangeShortcut = (days) => {
    // आज की तारीख लो
    const today = new Date()

    // 1 दिन future add करो
    const futureDate = new Date(today)
    futureDate.setDate(today.getDate() + 1)

    // Format करके दिखाओ
    const formattedFuture = formatDate(futureDate)

    // Start date निकालो
    const startDate = getDateDaysAgo(days)

    setFromDate(startDate)
    setToDate(formattedFuture) // Future date set
  }

  const handleThisMonth = () => {
    const today = new Date()

    // 1 दिन future add करो
    const futureDate = new Date(today)
    futureDate.setDate(today.getDate() + 1)

    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)

    setFromDate(formatDate(firstDay))
    setToDate(formatDate(futureDate)) // अब future date set होगी
  }

  // --- Form Handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const resetForm = () => {
    setFormData({
      amount: '',
      date: formatDate(new Date()),
      for_product: '',
      note: '',
      purchase: '',
      purchase_from: '',
      category: '',
      payment_mode: 'cash',
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (
      !formData.amount ||
      !formData.date ||
      !formData.for_product ||
      !formData.purchase_from ||
      !formData.category
    ) {
      toast.error('Please fill in all required fields (*)')
      return
    }
    setIsSubmitting(true)
    try {
      formData.date = new Date(formData.date)
      await api.createExpanse({
        ...formData,
        amount: parseFloat(formData.amount),
      })

      toast.success('Expense created successfully!')
      setShowForm(false)
      resetForm()
      // Refetch data for the current view after creating a new expense
      if (filterMode === 'single') {
        const { from, to } = getSingleDayRangeForAPI(selectedDate)
        await fetchExpenses(from, to)
      } else {
        await fetchExpenses(fromDate, toDate)
      }
    } catch (error) {
      console.error(error)
      toast.error(error?.message || 'Failed to create expense.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // --- Delete Handlers ---
  const handleDeleteClick = (expense) => {
    setDeleteDialog({
      isOpen: true,
      expenseId: expense.id,
      expenseName: expense.for_product,
      isDeleting: false,
    })
  }

  // const handleApprovalClick = async (id) => {
  //   setApprovingId(id)
  //   const updateExpenseData = { is_approval: true }
  //   try {
  //     const res = await api.updateExpanse(id, updateExpenseData)

  //     toast.success('Expense approved successfully!')

  //     // Refetch expenses after approval
  //     if (filterMode === 'single') {
  //       const { from, to } = getSingleDayRangeForAPI(selectedDate)
  //       await fetchExpenses(from, to)
  //     } else {
  //       await fetchExpenses(fromDate, toDate)
  //     }
  //   } catch (error) {
  //     console.error(error)
  //     toast.error(
  //       error?.response?.data?.message || 'Failed to approve expense.'
  //     )
  //   } finally {
  //     setApprovingId(null)
  //   }
  // }

  // const handleManagerApprovalClick = async (id) => {
  //   setApprovingId(id)
  //   const updateExpenseData = { is_approval: true }
  //   try {
  //     const res = await api.updateExpanse(id, updateExpenseData)

  //     toast.success('Expense approved successfully!')

  //     // Refetch expenses after approval
  //     if (filterMode === 'single') {
  //       const { from, to } = getSingleDayRangeForAPI(selectedDate)
  //       await fetchExpenses(from, to)
  //     } else {
  //       await fetchExpenses(fromDate, toDate)
  //     }
  //   } catch (error) {
  //     console.error(error)
  //     toast.error(
  //       error?.response?.data?.message || 'Failed to approve expense.'
  //     )
  //   } finally {
  //     setApprovingId(null)
  //   }
  // }

  // Admin Approval Handler (for high amount expenses >= 10000)
  // Admin Approval Handler (for high amount expenses >= 10000)
  const handleAdminApprovalClick = async (id) => {
    setApprovingId(id)
    const updateExpenseData = { admin_approval: true }
    try {
      await api.updateExpanse(id, updateExpenseData)
      toast.success('Admin approved successfully!')

      // Refetch expenses after approval
      if (filterMode === 'single') {
        const { from, to } = getSingleDayRangeForAPI(selectedDate)
        await fetchExpenses(from, to)
      } else {
        await fetchExpenses(fromDate, toDate)
      }
    } catch (error) {
      console.error(error)
      toast.error(
        error?.response?.data?.message || 'Failed to approve expense.',
      )
    } finally {
      setApprovingId(null)
    }
  }

  // Manager Approval Handler (for low amount OR after admin approval)
  const handleManagerApprovalClick = async (id) => {
    setApprovingId(id)
    const updateExpenseData = { is_approval: true }
    try {
      await api.updateExpanse(id, updateExpenseData)
      toast.success('Manager approved successfully!')

      // Refetch expenses after approval
      if (filterMode === 'single') {
        const { from, to } = getSingleDayRangeForAPI(selectedDate)
        await fetchExpenses(from, to)
      } else {
        await fetchExpenses(fromDate, toDate)
      }
    } catch (error) {
      console.error(error)
      toast.error(
        error?.response?.data?.message || 'Failed to approve expense.',
      )
    } finally {
      setApprovingId(null)
    }
  }

  const handleDeleteConfirm = async () => {
    setDeleteDialog((prev) => ({ ...prev, isDeleting: true }))
    try {
      await api.deleteExpanse(deleteDialog.expenseId)

      // Refetch expenses after deletion
      if (filterMode === 'single') {
        const { from, to } = getSingleDayRangeForAPI(selectedDate)
        await fetchExpenses(from, to)
      } else {
        await fetchExpenses(fromDate, toDate)
      }
      toast.success('Expense deleted successfully!')
      setDeleteDialog({
        isOpen: false,
        expenseId: null,
        expenseName: '',
        isDeleting: false,
      })
    } catch (error) {
      console.error('Error deleting expense:', error)
      toast.error(error?.response?.data?.message || 'Failed to delete expense.')
      setDeleteDialog((prev) => ({ ...prev, isDeleting: false }))
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialog({
      isOpen: false,
      expenseId: null,
      expenseName: '',
      isDeleting: false,
    })
  }

  // // --- Memoized calculations for performance ---

  // const filteredExpenses = useMemo(() => {
  //   if (!Array.isArray(expenses)) return []

  //   let result = expenses

  //   // 1. Scope Filter (Self mode)
  //   if (filterMode === 'self') {
  //     result = result.filter(
  //       (expense) => String(expense.spent_by_id) === String(id)
  //     )
  //   }

  //   // 2. Status Filter (Approved / Pending)
  //   if (statusFilter === 'approved') {
  //     result = result.filter((expense) => {
  //       const isHighAmount = Number(expense.amount) >= 10000
  //       if (isHighAmount) {
  //         return expense.admin_approved && expense.is_approval // दोनों true होने चाहिए
  //       }
  //       return expense.is_approval // सिर्फ manager approval
  //     })
  //   } else if (statusFilter === 'pending') {
  //     result = result.filter((expense) => {
  //       const isHighAmount = Number(expense.amount) >= 10000
  //       const isFullyApproved = expense.admin_approved && expense.is_approval
  //       return !isFullyApproved // जो पूरी तरह approved नहीं है
  //     })
  //   }

  //   // 3. Search Term Filter
  //   if (searchTerm) {
  //     result = result.filter((expense) =>
  //       Object.values(expense).some((val) =>
  //         String(val).toLowerCase().includes(searchTerm.toLowerCase())
  //       )
  //     )
  //   }

  //   return result
  // }, [expenses, searchTerm, filterMode, statusFilter, id])
  // --- Memoized calculations (Line 414 onwards) ---
  const filteredExpenses = useMemo(() => {
    if (!Array.isArray(expenses)) return []

    // Step 1: Start with all expenses
    let result = expenses

    // Step 2: Apply Scope Filter (Self vs All)
    // अगर filterMode 'self' है, तो सिर्फ मेरी ID वाले खर्चे फिल्टर करो
    if (filterMode === 'self') {
      result = result.filter(
        (expense) =>
          String(expense.spent_by_id) === String(id) ||
          String(expense.user_id) === String(id),
      )
    }

    // Step 3: Apply Status Filter (Approved / Pending) on top of the results
    if (statusFilter === 'approved') {
      result = result.filter((expense) => {
        const isHighAmount = Number(expense.amount) >= 10000
        if (isHighAmount) {
          // ₹10,000+ के लिए दोनों का अप्रूवल चाहिए
          return expense.admin_approved === true && expense.is_approval === true
        }
        // ₹10,000 से कम के लिए सिर्फ Manager (is_approval) काफी है
        return expense.is_approval === true
      })
    } else if (statusFilter === 'pending') {
      result = result.filter((expense) => {
        const isHighAmount = Number(expense.amount) >= 10000
        if (isHighAmount) {
          // ₹10,000+ तब तक पेंडिंग है जब तक दोनों (Admin + Manager) अप्रूव न कर दें
          const fullyApproved =
            expense.admin_approved === true && expense.is_approval === true
          return !fullyApproved
        }
        // ₹10,000 से कम तब तक पेंडिंग है जब तक is_approval false है
        return expense.is_approval === false || expense.is_approval === null
      })
    }

    // Step 4: Search filter (if any)
    if (searchTerm) {
      const s = searchTerm.toLowerCase()
      result = result.filter(
        (ex) =>
          ex.for_product.toLowerCase().includes(s) ||
          ex.category.toLowerCase().includes(s),
      )
    }

    return result
  }, [expenses, searchTerm, filterMode, statusFilter, id])

  const totalAmount = useMemo(() => {
    return filteredExpenses
      .reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0)
      .toFixed(2)
  }, [filteredExpenses])

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100'>
      <header className='bg-white shadow-md border-b border-gray-200  '>
        <div className='max-w-7xl mx-auto p-3 sm:p-4'>
          {/* Top Row: Title & Create Button */}
          <div className='flex flex-col sm:flex-row justify-between items-center gap-4 mb-4 sm:mb-6'>
            <div className='flex items-center w-full sm:w-auto justify-start'>
              <div className='bg-indigo-600 p-2 rounded-lg mr-3 shadow-lg'>
                <ReceiptIndianRupee className='h-6 w-6 sm:h-7 sm:w-7 text-white' />
              </div>
              <h1 className='text-xl sm:text-2xl font-black text-gray-900 tracking-tight'>
                Expense Tracker
              </h1>
            </div>

            <button
              onClick={() => setShowForm(true)}
              className='w-full sm:w-auto flex items-center justify-center px-6 py-2.5 bg-indigo-600 text-white font-bold rounded-xl shadow-indigo-200 shadow-lg hover:bg-indigo-700 hover:scale-105 transition-all duration-200 active:scale-95'
            >
              <Plus className='mr-2 h-5 w-5' />
              Create Expense
            </button>
          </div>

          {/* Filters Container */}
          <div className='bg-gray-50 p-3 sm:p-5 rounded-2xl border border-gray-200  space-y-4'>
            {/* Selection Row: View & Status */}
            <div className='flex flex-col lg:flex-row gap-4 justify-between lg:items-center'>
              {/* Group 1: View Selection (Daily, Range, Self) */}
              <div className='flex p-1 bg-gray-200 rounded-xl w-full lg:w-auto overflow-x-auto no-scrollbar'>
                {['single', 'range', 'self'].map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setFilterMode(mode)}
                    className={`flex-1 min-w-[80px] sm:min-w-[100px] px-3 py-2 text-xs sm:text-sm font-bold rounded-lg transition-all duration-200 ${
                      filterMode === mode
                        ? 'bg-white text-indigo-600 shadow-md'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {mode === 'single'
                      ? 'Daily'
                      : mode === 'range'
                        ? 'Range'
                        : 'Self'}
                  </button>
                ))}
              </div>

              {/* Group 2: Status Filter (All, Approved, Pending) */}
              <div className='flex p-1 bg-white border border-gray-200 rounded-xl w-full lg:w-auto shadow-sm overflow-x-auto no-scrollbar'>
                <button
                  onClick={() => setStatusFilter('all')}
                  className={`flex-1 min-w-[60px] px-3 py-2 text-[10px] sm:text-xs font-black rounded-lg transition-all ${
                    statusFilter === 'all'
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'text-gray-400'
                  }`}
                >
                  {' '}
                  ALL{' '}
                </button>

                <button
                  onClick={() => setStatusFilter('approved')}
                  className={`flex-1 min-w-[100px] px-3 py-2 text-[10px] sm:text-xs font-black rounded-lg flex items-center justify-center gap-1 transition-all ${
                    statusFilter === 'approved'
                      ? 'bg-green-600 text-white shadow-md'
                      : 'text-green-600 hover:bg-green-50'
                  }`}
                >
                  {' '}
                  <CheckCircle2 size={14} /> APPROVED{' '}
                </button>

                <button
                  onClick={() => setStatusFilter('pending')}
                  className={`flex-1 min-w-[100px] px-3 py-2 text-[10px] sm:text-xs font-black rounded-lg flex items-center justify-center gap-1 transition-all ${
                    statusFilter === 'pending'
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'text-orange-500 hover:bg-orange-50'
                  }`}
                >
                  {' '}
                  <Clock size={14} /> PENDING{' '}
                </button>
              </div>
            </div>

            {/* Date Controls Row */}
            <div className='pt-4 border-t border-gray-200'>
              {filterMode === 'single' ? (
                /* Daily Nav: Stacks on very small screens, side-by-side on sm+ */
                <div className='flex flex-col sm:flex-row items-center justify-between gap-4'>
                  <div className='flex items-center gap-2 bg-white p-1 rounded-xl border border-gray-200 shadow-sm w-full sm:w-auto justify-between sm:justify-start'>
                    <button
                      onClick={handlePrevDay}
                      className='p-2 hover:bg-gray-100 rounded-lg transition-colors text-indigo-600'
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <span className='text-sm font-bold text-gray-700 px-4'>
                      Navigate Day
                    </span>
                    <button
                      onClick={handleNextDay}
                      className='p-2 hover:bg-gray-100 rounded-lg transition-colors text-indigo-600'
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>

                  <input
                    type='date'
                    value={formatDate(selectedDate)}
                    onChange={(e) => handleDateSelect(e.target.value)}
                    className='w-full sm:w-auto border-gray-300 rounded-xl text-sm font-bold p-2.5 focus:ring-2 focus:ring-indigo-500 shadow-sm'
                  />
                </div>
              ) : (
                /* Range View: Multi-row on mobile, clean line on desktop */
                <div className='flex flex-col xl:flex-row gap-4 items-center justify-between'>
                  {/* Range Shortcuts - Grid on mobile */}
                  <div className='grid grid-cols-2 sm:flex flex-wrap gap-2 w-full'>
                    {[7, 15, 30].map((d) => (
                      <button
                        key={d}
                        onClick={() => handleRangeShortcut(d)}
                        className='px-3 py-2 bg-white border border-gray-200 text-[11px] sm:text-xs font-bold rounded-lg hover:border-indigo-500 hover:text-indigo-600 transition-all shadow-sm'
                      >
                        {d} Days
                      </button>
                    ))}
                    <button
                      onClick={handleThisMonth}
                      className='px-3 py-2 bg-white border border-gray-200 text-[11px] sm:text-xs font-bold rounded-lg hover:border-indigo-500 hover:text-indigo-600 shadow-sm'
                    >
                      {' '}
                      This Month{' '}
                    </button>
                  </div>

                  {/* Date Picker Inputs */}
                  <div className='flex items-center gap-2 w-full sm:w-auto bg-white p-2 rounded-xl border border-gray-200 shadow-sm'>
                    <input
                      type='date'
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className='border-none p-1 text-xs sm:text-sm font-medium focus:ring-0 w-full'
                    />
                    <span className='text-gray-400 font-bold'>-</span>
                    <input
                      type='date'
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className='border-none p-1 text-xs sm:text-sm font-medium focus:ring-0 w-full'
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* --- Main Content --- */}
      <main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8'>
        <div className='bg-white rounded-lg shadow-lg overflow-hidden'>
          <div className='px-4 sm:px-6 py-4 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2'>
            <h2 className='text-lg sm:text-xl font-semibold text-gray-900'>
              Expenses ({filteredExpenses.length})
            </h2>
            <div className='text-base sm:text-lg font-bold text-gray-800 bg-green-100 border border-green-300 px-3 py-1 rounded-md'>
              Total: ₹{totalAmount}
            </div>
          </div>

          <div className='divide-y divide-gray-200'>
            {loading ? (
              <div className='text-center p-12 text-gray-500 font-medium'>
                Loading expenses...
              </div>
            ) : filteredExpenses.length > 0 ? (
              <div className='p-4'>
                {filteredExpenses.map((expense) => (
                  <div
                    key={expense.id}
                    className='bg-white rounded-lg shadow border border-gray-200 mb-4'
                  >
                    {/* Status Bar */}
                    {(() => {
                      const isHighAmount = Number(expense.amount) >= 10000

                      // ========== HIGH AMOUNT EXPENSES (>= ₹10,000) ==========
                      if (isHighAmount) {
                        // ✅ Case 1: FULLY APPROVED (admin_approved=true AND is_approval=true)
                        // Status: Payment Completed (Green)
                        // Button: No button (both approved)
                        if (expense.admin_approved && expense.is_approval) {
                          return (
                            <div className='bg-green-500 px-4 py-2 flex items-center gap-2 text-white'>
                              <CheckCircle2 size={16} />
                              <span className='text-sm font-medium'>
                                Payment Completed
                              </span>
                            </div>
                          )
                        }

                        // 🟡 Case 2: ADMIN APPROVED, MANAGER PENDING (admin_approved=true, is_approval=false)
                        // Status: Pending by Manager (Yellow)
                        // Button: Only Manager sees "Manager Approve" button
                        if (expense.admin_approved && !expense.is_approval) {
                          return (
                            <div className='bg-yellow-500 px-4 py-2 flex items-center gap-2 text-white'>
                              <Clock size={16} />
                              <span className='text-sm font-medium'>
                                {role === 'manager' || role === 'director'
                                  ? 'Pending - Manager Approval Required'
                                  : role === 'admin'
                                    ? 'Pending by Manager'
                                    : 'Pending by Manager'}
                              </span>
                            </div>
                          )
                        }

                        // 🟠 Case 3: BOTH PENDING (admin_approved=false, is_approval=false)
                        // Status: Pending by Admin (Orange)
                        // Button: Only Admin sees "Admin Approve" button
                        if (!expense.admin_approved && !expense.is_approval) {
                          return (
                            <div className='bg-orange-500 px-4 py-2 flex items-center gap-2 text-white'>
                              <Clock size={16} />
                              <span className='text-sm font-medium'>
                                {role === 'admin'
                                  ? 'Pending - Admin Approval Required'
                                  : role === 'manager' || role === 'director'
                                    ? 'Pending by Admin'
                                    : 'Pending by Admin and Manager'}
                              </span>
                            </div>
                          )
                        }
                      }

                      // ========== LOW AMOUNT EXPENSES (< ₹10,000) ==========
                      else {
                        // ✅ Case 1: APPROVED (is_approval=true)
                        // Status: Payment Completed (Green)
                        // Button: No button
                        if (expense.is_approval) {
                          return (
                            <div className='bg-green-500 px-4 py-2 flex items-center gap-2 text-white'>
                              <CheckCircle2 size={16} />
                              <span className='text-sm font-medium'>
                                Payment Completed
                              </span>
                            </div>
                          )
                        }

                        // 🟠 Case 2: PENDING (is_approval=false)
                        // Status: Approval Pending (Orange)
                        // Button: Admin OR Manager can approve
                        if (!expense.is_approval) {
                          return (
                            <div className='bg-orange-500 px-4 py-2 flex items-center gap-2 text-white'>
                              <Clock size={16} />
                              <span className='text-sm font-medium'>
                                {role === 'admin' ||
                                role === 'manager' ||
                                role === 'director'
                                  ? 'Pending - Approval Required'
                                  : 'Approval Pending'}
                              </span>
                            </div>
                          )
                        }
                      }

                      return null
                    })()}

                    <div className='p-5'>
                      <div className='flex flex-col lg:flex-row justify-between gap-5'>
                        {/* Main Content */}
                        <div className='flex-1'>
                          {/* Amount and Product */}
                          <div className='flex items-center gap-4 mb-4 pb-4 border-b'>
                            <div className='bg-indigo-600 px-4 py-2 rounded'>
                              <p className='font-bold text-xl text-white'>
                                ₹
                                {Number(expense.amount).toLocaleString('en-IN')}
                              </p>
                            </div>
                            <div className='flex items-center text-sm text-gray-600'>
                              <User
                                size={14}
                                className='mr-1 text-indigo-600'
                              />
                              <span className='font-medium'>Spent By:</span>
                              <span className='ml-1 font-semibold text-gray-800'>
                                {expense.spent_by_name || expense.user_name}
                              </span>
                            </div>
                          </div>

                          {/* Details Grid */}
                          <div className='grid grid-cols-2 gap-3 mb-4'>
                            <div className='flex items-center gap-2'>
                              <CalendarDays
                                size={16}
                                className='text-blue-600'
                              />
                              <div>
                                <p className='text-xs text-gray-500'>Date</p>
                                <p className='text-sm font-semibold text-gray-800'>
                                  {formatDate(new Date(expense.expense_date))}
                                </p>
                              </div>
                            </div>

                            <div className='flex items-center gap-2'>
                              <Folder size={16} className='text-green-600' />
                              <div>
                                <p className='text-xs text-gray-500'>
                                  Category
                                </p>
                                <p className='text-sm font-semibold text-gray-800'>
                                  {expense.category}
                                </p>
                              </div>
                            </div>

                            <div className='flex items-center gap-2'>
                              <Building size={16} className='text-purple-600' />
                              <div>
                                <p className='text-xs text-gray-500'>Vendor</p>
                                <p className='text-sm font-semibold text-gray-800'>
                                  {expense.purchase_from}
                                </p>
                              </div>
                            </div>
                            <div className='flex items-center gap-2'>
                              <Building size={16} className='text-purple-600' />
                              <div>
                                <p className='text-xs text-gray-500'>Product</p>
                                <p className='text-sm font-semibold text-gray-800'>
                                  {expense.for_product}
                                </p>
                              </div>
                            </div>

                            <div className='flex items-center gap-2'>
                              <CreditCard
                                size={16}
                                className='text-orange-600'
                              />
                              <div>
                                <p className='text-xs text-gray-500'>Payment</p>
                                <p className='text-sm font-semibold text-gray-800'>
                                  {expense.payment_mode}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Note */}
                          {expense.note && (
                            <div className='bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r'>
                              <p className='text-sm text-gray-700'>
                                <FileText
                                  size={14}
                                  className='inline mr-1 text-blue-600'
                                />
                                {expense.note}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        {/* Action Buttons */}
                        <div className='flex lg:flex-col gap-2'>
                          {/* Approval Buttons Logic */}
                          {(() => {
                            const isHighAmount = Number(expense.amount) >= 10000

                            // HIGH AMOUNT (>= 10000): Admin first, then Manager
                            if (isHighAmount) {
                              // Step 1: Admin approval needed
                              if (!expense.admin_approved) {
                                return (
                                  <AuthorizedComponent requiredRole={['admin']}>
                                    <button
                                      onClick={() =>
                                        handleAdminApprovalClick(expense.id)
                                      }
                                      disabled={approvingId === expense.id}
                                      className={`px-4 py-2 rounded font-medium text-sm ${
                                        approvingId === expense.id
                                          ? 'bg-gray-300 text-gray-600'
                                          : 'bg-blue-500 text-white hover:bg-blue-600'
                                      }`}
                                    >
                                      {approvingId === expense.id ? (
                                        <span className='flex items-center gap-1'>
                                          <svg
                                            className='animate-spin h-4 w-4'
                                            viewBox='0 0 24 24'
                                          >
                                            <circle
                                              className='opacity-25'
                                              cx='12'
                                              cy='12'
                                              r='10'
                                              stroke='currentColor'
                                              strokeWidth='4'
                                              fill='none'
                                            />
                                            <path
                                              className='opacity-75'
                                              fill='currentColor'
                                              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                            />
                                          </svg>
                                          Wait...
                                        </span>
                                      ) : (
                                        'Admin Approve'
                                      )}
                                    </button>
                                  </AuthorizedComponent>
                                )
                              }

                              // Step 2: Manager approval (after admin)
                              if (
                                expense.admin_approved &&
                                !expense.is_approval
                              ) {
                                return (
                                  <AuthorizedComponent
                                    requiredRole={['manager']}
                                  >
                                    <button
                                      onClick={() =>
                                        handleManagerApprovalClick(expense.id)
                                      }
                                      disabled={approvingId === expense.id}
                                      className={`px-4 py-2 rounded font-medium text-sm ${
                                        approvingId === expense.id
                                          ? 'bg-gray-300 text-gray-600'
                                          : 'bg-yellow-500 text-white hover:bg-yellow-600'
                                      }`}
                                    >
                                      {approvingId === expense.id ? (
                                        <span className='flex items-center gap-1'>
                                          <svg
                                            className='animate-spin h-4 w-4'
                                            viewBox='0 0 24 24'
                                          >
                                            <circle
                                              className='opacity-25'
                                              cx='12'
                                              cy='12'
                                              r='10'
                                              stroke='currentColor'
                                              strokeWidth='4'
                                              fill='none'
                                            />
                                            <path
                                              className='opacity-75'
                                              fill='currentColor'
                                              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                            />
                                          </svg>
                                          Wait...
                                        </span>
                                      ) : (
                                        'Manager Approve'
                                      )}
                                    </button>
                                  </AuthorizedComponent>
                                )
                              }
                            }

                            // 🔥 CHANGED PART BELOW 🔥
                            // LOW AMOUNT (< 10000): only Manager can approve
                            else if (!expense.is_approval) {
                              return (
                                <AuthorizedComponent requiredRole={['manager']}>
                                  <button
                                    onClick={() =>
                                      handleManagerApprovalClick(expense.id)
                                    }
                                    disabled={approvingId === expense.id}
                                    className={`px-4 py-2 rounded font-medium text-sm ${
                                      approvingId === expense.id
                                        ? 'bg-gray-300 text-gray-600'
                                        : 'bg-yellow-500 text-white hover:bg-yellow-600'
                                    }`}
                                  >
                                    {approvingId === expense.id ? (
                                      <span className='flex items-center gap-1'>
                                        <svg
                                          className='animate-spin h-4 w-4'
                                          viewBox='0 0 24 24'
                                        >
                                          <circle
                                            className='opacity-25'
                                            cx='12'
                                            cy='12'
                                            r='10'
                                            stroke='currentColor'
                                            strokeWidth='4'
                                            fill='none'
                                          />
                                          <path
                                            className='opacity-75'
                                            fill='currentColor'
                                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                          />
                                        </svg>
                                        Wait...
                                      </span>
                                    ) : (
                                      'Approve'
                                    )}
                                  </button>
                                </AuthorizedComponent>
                              )
                            }

                            return null
                          })()}

                          {/* Delete Button */}
                          <button
                            onClick={() => handleDeleteClick(expense)}
                            className='p-2 text-red-500 hover:bg-red-50 rounded border border-red-300'
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='text-center p-12 text-gray-500 font-medium'>
                No expenses found for the selected period.
              </div>
            )}
          </div>
        </div>
      </main>

      {/* --- Modal Form --- */}
      {showForm && (
        <div className='fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 animate-fade-in'>
          <div className='bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto'>
            <div className='flex justify-between items-center p-4 border-b sticky top-0 bg-white'>
              <h2 className='text-lg font-semibold text-gray-800'>
                Create New Expense
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className='p-2 text-gray-500 hover:bg-gray-100 rounded-full'
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className='p-4 sm:p-6 space-y-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='relative'>
                  <CircleDollarSign
                    className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'
                    size={20}
                  />
                  <input
                    type='number'
                    name='amount'
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder='Amount *'
                    required
                    className='w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500'
                  />
                </div>
                <div className='relative'>
                  <CalendarDays
                    className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'
                    size={20}
                  />
                  <input
                    type='date'
                    name='date'
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    max={formatDate(new Date())}
                    className='w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500'
                  />
                </div>
              </div>
              <div className='relative'>
                <ShoppingBag
                  className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'
                  size={20}
                />
                <input
                  type='text'
                  name='for_product'
                  value={formData.for_product}
                  onChange={handleInputChange}
                  placeholder='Expense for (Product/Service) *'
                  required
                  className='w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500'
                />
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='relative'>
                  <Building
                    className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'
                    size={20}
                  />
                  <input
                    type='text'
                    name='purchase_from'
                    value={formData.purchase_from}
                    onChange={handleInputChange}
                    placeholder='Purchased From *'
                    required
                    className='w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500'
                  />
                </div>
                <div className='relative'>
                  <FileText
                    className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'
                    size={20}
                  />
                  <input
                    type='text'
                    name='category'
                    value={formData.category}
                    onChange={handleInputChange}
                    placeholder='Category *'
                    required
                    className='w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500'
                  />
                </div>
              </div>
              <div className='relative'>
                <CreditCard
                  className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'
                  size={20}
                />
                <select
                  name='payment_mode'
                  value={formData.payment_mode}
                  onChange={handleInputChange}
                  className='w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 appearance-none bg-white'
                >
                  <option value='cash'>Cash</option>
                  <option value='upi'>UPI</option>
                </select>
              </div>
              <div>
                <textarea
                  name='note'
                  value={formData.note}
                  onChange={handleInputChange}
                  placeholder='Add a note (optional)'
                  className='w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 resize-none'
                  rows='3'
                ></textarea>
              </div>
              <div className='flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t'>
                <button
                  type='button'
                  onClick={() => setShowForm(false)}
                  className='w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={isSubmitting}
                  className='w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed transition-colors'
                >
                  {isSubmitting ? 'Saving...' : 'Save Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        expenseName={deleteDialog.expenseName}
        isDeleting={deleteDialog.isDeleting}
      />
    </div>
  )
}

export default Expense
